import { useState } from "react";
import { toast } from "sonner";
import { API, api, errMsg, fmtDate, fmtDateTime, money, HEARING_TYPES, hearingType } from "@/lib/api";
import { Badge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Trash2, Upload, ShieldCheck, CalendarPlus, CalendarClock, Ban } from "lucide-react";
import { DictateButton } from "@/components/DictateButton";
import { SignedPdf } from "@/components/SignedPdf";
import { MultiPdfPicker, checkPdfs, uploadFiling } from "@/components/MultiPdfPicker";
import { F, btn } from "@/components/CaseBits";

export const VerifyPanel = ({ caseId, proposed, onDone }) => {
  const [number, setNumber] = useState(proposed || "");
  const [remarks, setRemarks] = useState("");
  const go = async (action) => {
    if (action === "verify" && !number) return toast.error("Assign your file number");
    try {
      await api.post(`/cases/${caseId}/verify`, { case_number: number || "NA", action, remarks });
      toast.success(action === "verify" ? "Reference verified and numbered" : "Reference returned");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-8 border border-blue-900/40 bg-blue-50 dark:bg-blue-950/40 p-6" data-testid="verify-panel">
      <p className="label-caps flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Verification required</p>
      <p className="text-sm mt-3 max-w-2xl">
        Confirm this reference and assign the number under which it stands on your file. Parties will be intimated.
      </p>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
        <F label="Your file number" value={number} onChange={setNumber} testId="verify-number-input" placeholder="ARB/12/2026" />
        <F label="Remarks" value={remarks} onChange={setRemarks} testId="verify-remarks-input" />
        <div className="flex gap-3">
          <Button data-testid="verify-case-button" onClick={() => go("verify")} className={btn}>Verify</Button>
          <Button data-testid="reject-case-button" onClick={() => go("reject")}
            className="rounded-none bg-red-800 hover:bg-red-700 text-white uppercase tracking-widest text-xs h-10">Return</Button>
        </div>
      </div>
    </div>
  );
};
export const ProceedingRow = ({ p, isArb, isAdv, onReload, canDownload, caseId }) => {
  const [order, setOrder] = useState(p.daily_order || "");
  const [next, setNext] = useState("");
  const [reason, setReason] = useState("");
  const [adjFiles, setAdjFiles] = useState([]);
  const [adjProgress, setAdjProgress] = useState(null);
  const [moving, setMoving] = useState(false);
  const [move, setMove] = useState({ scheduled_at: "", reason: "" });

  const reschedule = async () => {
    if (!move.scheduled_at) return toast.error("Pick the new date and time");
    try {
      await api.post(`/proceedings/${p.id}/reschedule`, move);
      toast.success("Sitting moved — parties alerted, calendars updated");
      setMoving(false);
      onReload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const cancel = async () => {
    const why = window.prompt("Reason for cancelling this sitting (optional)") ;
    if (why === null) return;
    try {
      await api.post(`/proceedings/${p.id}/cancel`, { reason: why || undefined });
      toast.success("Sitting cancelled — parties alerted, calendars updated");
      onReload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveOrder = async () => {
    try {
      await api.put(`/proceedings/${p.id}`, { daily_order: order, status: "held", next_date: next || undefined });
      toast.success("Daily order uploaded — parties intimated");
      onReload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const seekAdjournment = async () => {
    if (!reason) return toast.error("State the reason for adjournment");
    const bad = checkPdfs(adjFiles);
    if (bad) return toast.error(bad);
    try {
      await api.post("/adjournments", { proceeding_id: p.id, reason });
      const rows = await uploadFiling(p.case_id || caseId, `Adjournment application — sitting of ${fmtDate(p.scheduled_at)}`, "application", adjFiles, { onProgress: setAdjProgress });
      toast.success(`Adjournment sought${rows.length ? ` with ${rows.length} PDF(s)` : ""} — parties and tribunal intimated`);
      setReason(""); setAdjFiles([]); setAdjProgress(null);
      onReload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="px-6 py-5 border-b border-border last:border-0" data-testid={`proceeding-row-${p.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
            <Badge className={hearingType(p.hearing_type).cls} data-testid={`hearing-type-badge-${p.id}`}>{hearingType(p.hearing_type).label}</Badge>
            {p.purpose}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {fmtDateTime(p.scheduled_at)} · {p.mode}{p.venue ? ` · ${p.venue}` : ""}
            {p.next_date ? ` · next: ${fmtDate(p.next_date)}` : ""}
          </p>
          {p.moves?.length > 0 && (
            <p className="text-[11px] text-amber-800 dark:text-amber-400 mt-1" data-testid={`moved-note-${p.id}`}>
              Moved from {fmtDateTime(p.moves[p.moves.length - 1].from)}{p.moves[p.moves.length - 1].reason ? ` — ${p.moves[p.moves.length - 1].reason}` : ""}
            </p>
          )}
          {p.status === "cancelled" && (
            <p className="text-[11px] text-red-800 dark:text-red-400 mt-1" data-testid={`cancelled-note-${p.id}`}>
              Cancelled{p.cancel_reason ? ` — ${p.cancel_reason}` : ""}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isArb && p.status === "scheduled" && (
            <>
              <button data-testid={`move-proceeding-${p.id}`} onClick={() => setMoving((m) => !m)}
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-blue-900 dark:text-blue-400 hover:underline">
                <CalendarClock className="h-3.5 w-3.5" /> Move
              </button>
              <button data-testid={`cancel-proceeding-${p.id}`} onClick={cancel}
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-red-800 hover:underline">
                <Ban className="h-3.5 w-3.5" /> Cancel
              </button>
            </>
          )}
          <a href={`${API}/proceedings/${p.id}/calendar.ics`} data-testid={`ics-proceeding-${p.id}`}
            title="Add to calendar (.ics)"
            className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-blue-900 dark:text-blue-400 hover:underline">
            <CalendarPlus className="h-3.5 w-3.5" /> Calendar
          </a>
          <Badge className="bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200">{p.status}</Badge>
          {isArb && (
            <button data-testid={`delete-proceeding-${p.id}`}
              onClick={async () => { await api.delete(`/proceedings/${p.id}`); onReload(); }}
              className="text-red-800 hover:text-red-600 transition-colors duration-200"><Trash2 className="h-4 w-4" /></button>
          )}
        </div>
      </div>

      {moving && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-4 items-end border border-blue-900/30 bg-blue-50 dark:bg-blue-950/30 p-4" data-testid={`move-form-${p.id}`}>
          <F label="New date & time" type="datetime-local" value={move.scheduled_at} onChange={(v) => setMove((m) => ({ ...m, scheduled_at: v }))} testId={`move-datetime-${p.id}`} />
          <F label="Reason (shared with parties)" value={move.reason} onChange={(v) => setMove((m) => ({ ...m, reason: v }))} testId={`move-reason-${p.id}`} />
          <Button data-testid={`move-save-${p.id}`} onClick={reschedule} className={btn}>Move sitting</Button>
        </div>
      )}

      {p.daily_order && (
        <div className="mt-4 border-l-2 border-blue-900 pl-4">
          <p className="label-caps">Daily order</p>
          <p className="text-sm mt-2 whitespace-pre-wrap" data-testid={`daily-order-${p.id}`}>{p.daily_order}</p>
        </div>
      )}

      <div className="mt-4">
        <SignedPdf kind="proceeding" targetId={p.id} documentId={canDownload ? p.order_document_id : null}
          canUpload={isArb} onDone={onReload} testId={`proceeding-pdf-${p.id}`} label="signed daily order" />
        {!canDownload && p.order_document_id && (
          <p className="text-[11px] text-muted-foreground" data-testid={`download-locked-${p.id}`}>
            A signed PDF is on record. Upgrade to full access to download it.
          </p>
        )}
      </div>

      {isArb && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[2fr_1fr_auto] gap-4 items-end">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label className="label-caps">Dictate or type the daily order</Label>
              <DictateButton testId={`dictate-${p.id}`}
                onTranscript={(t) => setOrder((prev) => (prev ? `${prev.trim()} ${t}` : t))} />
            </div>
            <Textarea data-testid={`order-input-${p.id}`} rows={3} className="rounded-none" value={order}
              onChange={(e) => setOrder(e.target.value)} placeholder="Heard both sides. Claimant to file rejoinder…" />
          </div>
          <F label="Next date" type="date" value={next} onChange={setNext} testId={`next-date-${p.id}`} />
          <Button data-testid={`save-order-${p.id}`} onClick={saveOrder} className={btn}>Upload order</Button>
        </div>
      )}

      {isAdv && p.status === "scheduled" && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_auto] gap-4 items-end">
            <F label="Seek adjournment (reason)" value={reason} onChange={setReason} testId={`adjourn-reason-${p.id}`} />
            <Button data-testid={`seek-adjournment-${p.id}`} onClick={seekAdjournment}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white uppercase tracking-widest text-xs h-10">Request</Button>
          </div>
          <MultiPdfPicker files={adjFiles} onChange={setAdjFiles} label="Attach the adjournment application / medical certificate (PDF)" testId={`adjourn-pdfs-${p.id}`} progress={adjProgress} />
        </div>
      )}
    </div>
  );
};
export const TagForm = ({ label, cta, onSubmit, testId }) => {
  const [email, setEmail] = useState("");
  return (
    <form data-testid={`${testId}-form`}
      onSubmit={async (e) => { e.preventDefault(); if (await onSubmit(email)) setEmail(""); }}
      className="flex flex-col sm:flex-row gap-3 items-end">
      <div className="flex-1 w-full">
        <F label={label} type="email" required value={email} onChange={setEmail} testId={`${testId}-input`} placeholder="name@example.com" />
      </div>
      <Button type="submit" className={btn} data-testid={`${testId}-button`}>{cta}</Button>
    </form>
  );
};

export const ProceedingForm = ({ onSubmit }) => {
  const blank = { purpose: "", scheduled_at: "", mode: "physical", venue: "", hearing_type: "miscellaneous" };
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <form data-testid="proceeding-form" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 items-end"
      onSubmit={async (e) => {
        e.preventDefault();
        if (await onSubmit(form)) setForm(blank);
      }}>
      <div className="space-y-2">
        <Label className="label-caps">Hearing type</Label>
        <Select value={form.hearing_type} onValueChange={(v) => set("hearing_type", v)}>
          <SelectTrigger className="rounded-none h-10" data-testid="proceeding-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {HEARING_TYPES.map((h) => <SelectItem key={h.value} value={h.value} data-testid={`hearing-type-${h.value}`}>{h.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <F label="Purpose" required value={form.purpose} onChange={(v) => set("purpose", v)} testId="proceeding-purpose-input" placeholder="Directions" />
      <F label="Date & time" type="datetime-local" required value={form.scheduled_at} onChange={(v) => set("scheduled_at", v)} testId="proceeding-datetime-input" />
      <div className="space-y-2">
        <Label className="label-caps">Mode</Label>
        <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
          <SelectTrigger className="rounded-none h-10" data-testid="proceeding-mode-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="physical">Physical</SelectItem>
            <SelectItem value="virtual">Virtual</SelectItem>
            <SelectItem value="hybrid">Hybrid</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <F label="Venue / link" value={form.venue} onChange={(v) => set("venue", v)} testId="proceeding-venue-input" />
      <Button type="submit" className={btn} data-testid="post-proceeding-button">Post case</Button>
    </form>
  );
};

export const DocForm = ({ caseId, onDone, isArb }) => {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("pleading");
  const [files, setFiles] = useState([]);
  const [merge, setMerge] = useState(false);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) return toast.error("Attach at least one PDF");
    const bad = checkPdfs(files);
    if (bad) return toast.error(bad);
    setBusy(true);
    try {
      const rows = await uploadFiling(caseId, title, category, files, { merge, onProgress: setProgress });
      toast.success(merge && files.length > 1 ? "Bundle created and added to the case documents" : `${rows.length} PDF(s) added to the case documents`);
      setTitle(""); setFiles([]); setMerge(false); setProgress(null);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
      setProgress(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end" data-testid="document-form">
      <F label="Title" required value={title} onChange={setTitle} testId="document-title-input" placeholder="Rejoinder / Written statement / Application" />
      <div className="space-y-2">
        <Label className="label-caps">Category</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="rounded-none h-10" data-testid="document-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(isArb ? ["pleading", "application", "evidence", "order", "interim_order", "award", "correspondence"]
              : ["pleading", "application", "evidence", "correspondence"]).map((c) => (
              <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="sm:col-span-2 lg:col-span-4">
        <MultiPdfPicker files={files} onChange={setFiles} testId="document-pdfs" required progress={progress} merge={merge} onMergeChange={setMerge} />
      </div>
      <Button type="submit" disabled={busy} data-testid="document-upload-button" className={btn}>
        <Upload className="h-3.5 w-3.5 mr-2" />{busy ? "Uploading…" : `Upload${files.length > 1 ? ` ${files.length} PDFs` : ""}`}
      </Button>
    </form>
  );
};

export const OrderForm = ({ onSubmit }) => {
  const init = { kind: "procedural_order", number: "", title: "", issued_on: "", summary: "", body: "" };
  const [form, setForm] = useState(init);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <form data-testid="order-form" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end"
      onSubmit={async (e) => { e.preventDefault(); if (await onSubmit(form)) setForm(init); }}>
      <div className="space-y-2 sm:col-span-2 lg:col-span-5">
        <Label className="label-caps">Order text — typed here, a PDF is generated and saved to the case documents on Record</Label>
        <Textarea data-testid="order-body-input" rows={8} className="rounded-none font-serif text-[15px] leading-relaxed"
          placeholder="Type the order or award in full. Header (case number, parties, date) and footer (arbitrator) are added automatically."
          value={form.body} onChange={(e) => set("body", e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label className="label-caps">Kind</Label>
        <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
          <SelectTrigger className="rounded-none h-10" data-testid="order-kind-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="procedural_order">Procedural order</SelectItem>
            <SelectItem value="interim_order">Interim order</SelectItem>
            <SelectItem value="interim_award">Interim award</SelectItem>
            <SelectItem value="final_award">Final award</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <F label="Number" value={form.number} onChange={(v) => set("number", v)} testId="order-number-input" placeholder="PO-3" />
      <F label="Title" required value={form.title} onChange={(v) => set("title", v)} testId="order-title-input" />
      <F label="Issued on" type="date" value={form.issued_on} onChange={(v) => set("issued_on", v)} testId="order-date-input" />
      <Button type="submit" className={btn} data-testid="submit-order-button">Record</Button>
    </form>
  );
};

export const InvoiceForm = ({ onSubmit }) => {
  const init = { invoice_number: "", payable_to: "", amount: "", currency: "INR", due_on: "", particulars: "", status: "unpaid" };
  const [form, setForm] = useState(init);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <form data-testid="invoice-form" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end"
      onSubmit={async (e) => {
        e.preventDefault();
        if (await onSubmit({ ...form, amount: Number(form.amount) })) setForm(init);
      }}>
      <F label="Invoice no." required value={form.invoice_number} onChange={(v) => set("invoice_number", v)} testId="invoice-number-input" />
      <F label="Payable by" required value={form.payable_to} onChange={(v) => set("payable_to", v)} testId="invoice-payee-input" />
      <F label="Amount" type="number" required value={form.amount} onChange={(v) => set("amount", v)} testId="invoice-amount-input" />
      <F label="Due on" type="date" value={form.due_on} onChange={(v) => set("due_on", v)} testId="invoice-due-input" />
      <Button type="submit" className={btn} data-testid="submit-invoice-button">Raise</Button>
    </form>
  );
};
